import { not } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import EligibilityFileSelector from '../eligibility-file-selector.gts';
import type Store from '@ember-data/store';
import type CompanyModel from '../../models/company.ts';
import type RouterService from '@ember/routing/router-service';
import type { FileQueueService } from 'ember-file-upload';
import type EligibilityFileJobLogModel from '../../models/eligibility-file-job-log.ts';
import { Footer, Modal } from 'tio-ui/components/modal';

interface TioEligibilityUploadModalSignature {
  Args: {
    cancel: () => void;
    company: CompanyModel;
    onCancel: () => void;
    show: boolean;
    showRoute: string;
    toggle: () => void;
  };
  Element: HTMLElement;
}

export default class TioEligibilityUploadModalComponent extends Component<TioEligibilityUploadModalSignature> {
  @service declare store: Store;
  @service declare router: RouterService;
  @service declare fileQueue: FileQueueService;

  @tracked currentUploadUrl = '';
  @tracked currentJob?: EligibilityFileJobLogModel;
  @tracked uploading = false;

  toggleFileSelected = (item: EligibilityFileJobLogModel) => {
    this.currentJob = item;
  };

  submit = async () => {
    // We don't really care about waiting on upload. The transition will handle displaying a status.
    this.uploading = true;
    await this.uploadEligibilityFile();
    this.args.toggle();
    this.router.transitionTo(this.args.showRoute, this.currentJob?.id);
  };

  cancel = () => {
    // This is not actually called from UI which means the abort is not actually needed.
    this.args.onCancel?.();
    this.abortEligibilityFile();
  };

  abortEligibilityFile = async () => {
    const { legacyId, id } = this.args.company;

    await this.store
      .adapterFor('eligibility-file-job-log')
      .abortJob(legacyId, id, this.currentJob?.processId);
  };

  async uploadEligibilityFile() {
    const queue = this.fileQueue.find('eligibility-upload-queue');

    // Return a new Promise
    return new Promise<boolean>((resolve, reject) => {
      // TODO: There is a weird timing bug with the file upload modifying a tracked prop twice in the same loop.
      setTimeout(async () => {
        if (!this.currentJob?.uploadUrl) {
          return;
        }
        try {
          await queue?.files[0]?.uploadBinary(this.currentJob?.uploadUrl, {
            method: 'PUT',
          });
          resolve(true);
        } catch (_error) {
          reject(false);
        }
      }, 1);
    });
  }

  <template>
    <Modal @isOpen={{true}} @onClose={{@cancel}} class="min-h-64" @backdrop="faded" as |m|>
      <m.Header>
        {{t "eligibility_files.header"}}
      </m.Header>
      <m.Body>
        <EligibilityFileSelector
          class="flex flex-col items-center justify-center min-w-96 w-full"
          @company={{@company}}
          @toggleFileSelected={{this.toggleFileSelected}}
        />
        <p class="text-sm text-tio-gray-600">{{t "eligibility_files.warning_message"}}</p>
      </m.Body>
      <m.Footer>
        <Footer
          @isRunning={{this.uploading}}
          @onCancel={{@cancel}}
          @onSubmit={{this.submit}}
          @submitDisabled={{not this.currentJob}}
          @submitText={{t "next"}}
        />
      </m.Footer>
    </Modal>
  </template>
}
